import { NavLink } from 'react-router-dom';
import { LogOut, ChevronDown } from 'lucide-react';

import { PRIMARY_MODULES, ADMIN_MODULES, type ModuleDef } from '@/lib/modules';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export function Sidebar() {
  const { user, logout } = useAuth();

  return (
    <aside className="hidden sm:flex fixed inset-y-0 left-0 w-72 flex-col border-r border-border bg-sidebar text-sidebar-foreground">
      {/* Logo — centered, hero-sized */}
      <div className="h-28 flex items-center justify-center border-b border-border px-4">
        <img src="/kejja.svg" alt="KEJJA" className="h-16 w-auto object-contain dark:hidden" />
        <img src="/kejja-white.svg" alt="KEJJA" className="h-16 w-auto object-contain hidden dark:block" />
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
        <NavSection items={PRIMARY_MODULES} />
        <Separator />
        <NavSection items={ADMIN_MODULES} />
      </nav>

      <div className="p-3 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-sidebar-accent transition-colors text-left">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatarUrl ?? undefined} alt={user?.displayName} />
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                  {user?.displayName?.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.displayName}</p>
                <p className="text-[11px] text-muted-foreground truncate">{user?.email}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

function NavSection({ items }: { items: ModuleDef[] }) {
  return (
    <ul className="space-y-0.5">
      {items.map((m) => (
        <li key={m.key}>
          <NavLink
            to={m.route}
            end={m.route === '/dashboard'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors',
                isActive
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )
            }
          >
            <m.icon className="w-4 h-4" />
            <span>{m.label}</span>
            {m.status === 'coming_soon' && (
              <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                soon
              </span>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
